import React, { useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import GarageIcon from '@mui/icons-material/Garage';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandymanIcon from '@mui/icons-material/Handyman';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import Header from 'components/Header/Header.js';
import HomeFooter from 'components/Footer/HomeFooter.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colors } from 'components/Theme/WebTheme.js';
import { FONT_FAMILY } from '../common/sharedFunctions';
import { usePanelDark, PANEL_DARK_BG, PANEL_DARK_TEXT } from '../common/panelTheme';
import {
  BADGE_STYLES,
  LAUNCHES_THEME,
  OWNER_SERVICES,
  PROVIDER_SERVICES,
  ROADMAP,
} from '../common/launchesContent';

const ICON_MAP = {
  Build: BuildIcon,
  TireRepair: TireRepairIcon,
  BatteryChargingFull: BatteryChargingFullIcon,
  LocalGasStation: LocalGasStationIcon,
  LocalCarWash: LocalCarWashIcon,
  VpnKey: VpnKeyIcon,
  Garage: GarageIcon,
  EventAvailable: EventAvailableIcon,
  LocalShipping: LocalShippingIcon,
  Handyman: HandymanIcon,
  HomeRepairService: HomeRepairServiceIcon,
  Business: BusinessIcon,
};

const pillBtn = {
  fontFamily: FONT_FAMILY,
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '999px',
  px: 3.5,
  py: 1.2,
  boxShadow: 'none',
};

function StatusBadge({ label, tone, dark }) {
  const s = BADGE_STYLES[tone] || BADGE_STYLES.soon;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: dark ? 'rgba(255,255,255,0.08)' : s.bg,
        color: dark ? '#a7f3d0' : s.text,
        border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : s.border}`,
        fontFamily: FONT_FAMILY,
        fontWeight: 600,
        height: 26,
        borderRadius: '999px',
        fontSize: 12,
      }}
    />
  );
}

function SectionHeading({ kicker, title, subtitle, dark }) {
  return (
    <Box sx={{ mb: 2.5, mt: { xs: 3, md: 4 } }}>
      {kicker ? (
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: dark ? '#6ee7b7' : LAUNCHES_THEME.accent,
            mb: 0.75,
          }}
        >
          {kicker}
        </Typography>
      ) : null}
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontWeight: 800,
          fontSize: { xs: 24, md: 30 },
          lineHeight: 1.15,
          color: dark ? PANEL_DARK_TEXT : colors.BLACK,
          mb: subtitle ? 1 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontFamily: FONT_FAMILY, color: dark ? 'rgba(255,255,255,0.72)' : LAUNCHES_THEME.muted, lineHeight: 1.65, maxWidth: 720 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

export function UpcomingLaunchesContent({ embedded = false }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const providersRef = useRef(null);
  const urgencyRef = useRef(null);
  const panelDark = usePanelDark();
  const dark = embedded && panelDark;
  const isDriver = auth?.profile?.usertype === 'driver';

  const pageBg = dark ? PANEL_DARK_BG : LAUNCHES_THEME.pageBg;
  const cardBg = dark ? 'rgba(255,255,255,0.04)' : LAUNCHES_THEME.cardBg;
  const cardBorder = dark ? 'rgba(255,255,255,0.1)' : LAUNCHES_THEME.cardBorder;
  const textPrimary = dark ? PANEL_DARK_TEXT : colors.BLACK;
  const muted = dark ? 'rgba(255,255,255,0.68)' : LAUNCHES_THEME.muted;

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onRegister = () => navigate('/contact-us');
  const onProfileCta = () => {
    if (!auth?.profile?.uid) {
      navigate('/login');
      return;
    }
    if (isDriver) navigate('/cars');
    else navigate('/profile');
  };

  const FeatureCard = ({ icon, title, desc, status, tone, showOpen, index = 0 }) => {
    const Icon = ICON_MAP[icon] || BuildIcon;
    const delayClass = `ai-rise-${(index % 4) + 1}`;
    return (
      <Box
        className={`ai-glow-card ai-rise ${delayClass}`}
        sx={{
          border: `1px solid ${cardBorder}`,
          borderRadius: '20px',
          p: 2.5,
          backgroundColor: cardBg,
          boxShadow: dark ? 'none' : LAUNCHES_THEME.cardShadow,
          height: '100%',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
          '&:hover': {
            borderColor: dark ? 'rgba(6,200,109,0.35)' : 'rgba(6,200,109,0.25)',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', mb: 1.25 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '16px',
              background: dark
                ? 'linear-gradient(135deg, rgba(6,200,109,0.22), rgba(34,211,238,0.12))'
                : 'linear-gradient(135deg, rgba(6,200,109,0.14), rgba(34,211,238,0.08))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: LAUNCHES_THEME.accent, fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 17, color: textPrimary, mb: 0.75 }}>
              {title}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {status ? <StatusBadge label={status} tone={tone} dark={dark} /> : null}
              {showOpen ? (
                <StatusBadge
                  label={t('launches_open_call', { defaultValue: 'Convocatoria abierta' })}
                  tone="open"
                  dark={dark}
                />
              ) : null}
            </Stack>
          </Box>
        </Box>
        <Typography sx={{ fontFamily: FONT_FAMILY, color: muted, fontSize: 14, lineHeight: 1.6 }}>
          {desc}
        </Typography>
      </Box>
    );
  };

  const heroBlock = embedded ? null : (
    <Box
      className="ai-sheen"
      sx={{
        background: LAUNCHES_THEME.heroGradient,
        borderRadius: { xs: 0, md: '28px' },
        px: { xs: 2.5, md: 4 },
        py: { xs: 4, md: 5 },
        mb: { xs: 3, md: 4 },
        mx: { xs: 0, md: 0 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -60,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6,200,109,0.25) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <RocketLaunchIcon sx={{ color: '#6ee7b7', fontSize: 28 }} />
        <Typography
          className="ai-gradient-text"
          sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: { xs: 26, md: 38 }, lineHeight: 1.1 }}
        >
          {t('launches_screen_title', { defaultValue: 'Próximos lanzamientos Movex' }).replace('🚀 ', '')}
        </Typography>
      </Stack>
      <Typography sx={{ fontFamily: FONT_FAMILY, color: 'rgba(255,255,255,0.78)', mb: 2.5, lineHeight: 1.65, maxWidth: 680, fontSize: { xs: 15, md: 17 } }}>
        {t('launches_screen_subtitle', {
          defaultValue: 'Estamos construyendo el ecosistema inteligente para propietarios de vehículos, conductores y empresas de asistencia vial.',
        })}
      </Typography>

      <Box
        sx={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '20px',
          p: 2.5,
          mb: 2.5,
          maxWidth: 720,
        }}
      >
        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 20, color: '#fff', mb: 1 }}>
          {t('launches_hero_title', { defaultValue: 'Tu vehículo. Tus servicios. Una sola app.' })}
        </Typography>
        <Typography sx={{ fontFamily: FONT_FAMILY, color: 'rgba(255,255,255,0.72)', mb: 1.5, lineHeight: 1.6 }}>
          {t('launches_hero_text', {
            defaultValue: 'Movex empezó con grúas, pero está evolucionando hacia una plataforma completa para administrar, proteger, mantener y resolver todo lo relacionado con tu vehículo.',
          })}
        </Typography>
        <StatusBadge label={t('launches_network_badge', { defaultValue: 'Red en expansión' })} tone="launch" dark />
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          variant="contained"
          onClick={() => scrollTo(urgencyRef)}
          sx={{
            ...pillBtn,
            background: `linear-gradient(135deg, ${LAUNCHES_THEME.accent} 0%, ${LAUNCHES_THEME.accentDark} 100%)`,
            color: '#fff',
            '&:hover': { background: `linear-gradient(135deg, ${LAUNCHES_THEME.accentDark} 0%, #078a2a 100%)` },
          }}
        >
          {t('launches_cta_join', { defaultValue: 'Quiero ser parte' })}
        </Button>
        <Button
          variant="outlined"
          onClick={() => scrollTo(providersRef)}
          sx={{
            ...pillBtn,
            borderColor: 'rgba(255,255,255,0.35)',
            color: '#fff',
            '&:hover': { borderColor: '#6ee7b7', backgroundColor: 'rgba(255,255,255,0.06)' },
          }}
        >
          {t('launches_cta_opportunities', { defaultValue: 'Ver oportunidades' })}
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', pb: 4, backgroundColor: pageBg, borderRadius: embedded ? 2 : 0 }}>
      {heroBlock}

      <SectionHeading
        kicker="Propietarios"
        title={t('launches_owners_title', { defaultValue: 'Para dueños de vehículos' }).replace('🚗 ', '')}
        subtitle={t('launches_owners_sub', {
          defaultValue: 'Muy pronto podrás resolver más necesidades de tu vehículo desde Movex, sin llamadas interminables y con seguimiento desde la app.',
        })}
        dark={dark}
      />
      <Grid container spacing={2.5} sx={{ mb: 1 }}>
        {OWNER_SERVICES.map((item, index) => (
          <Grid item xs={12} sm={6} key={item.title}>
            <FeatureCard {...item} index={index} />
          </Grid>
        ))}
      </Grid>

      <Box ref={providersRef}>
        <SectionHeading
          kicker="Proveedores"
          title={t('launches_providers_title', { defaultValue: 'Para proveedores y empresas' }).replace('🛠️ ', '')}
          subtitle={t('launches_providers_sub', {
            defaultValue: 'Movex está creando una red donde profesionales y empresas podrán recibir oportunidades según su ubicación, disponibilidad, reputación y cobertura.',
          })}
          dark={dark}
        />
      </Box>
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {PROVIDER_SERVICES.map((item, index) => (
          <Grid item xs={12} sm={6} key={item.title}>
            <FeatureCard {...item} showOpen index={index} />
          </Grid>
        ))}
      </Grid>

      <Button
        variant="contained"
        startIcon={<AppRegistrationIcon />}
        onClick={onRegister}
        sx={{
          ...pillBtn,
          mb: 1,
          background: dark ? 'rgba(6,200,109,0.2)' : `linear-gradient(135deg, ${LAUNCHES_THEME.accent} 0%, ${LAUNCHES_THEME.accentDark} 100%)`,
          color: '#fff',
          border: dark ? '1px solid rgba(6,200,109,0.4)' : 'none',
        }}
      >
        {t('launches_register_service', { defaultValue: 'Registrar mi servicio' })}
      </Button>

      <SectionHeading
        kicker="Hoja de ruta"
        title={t('launches_vision_title', { defaultValue: 'Lo que viene' }).replace('🌎 ', '')}
        dark={dark}
      />
      <Box
        className="ai-glow-card"
        sx={{
          border: `1px solid ${cardBorder}`,
          borderRadius: '24px',
          p: { xs: 2, md: 3 },
          mb: 2,
          backgroundColor: cardBg,
          boxShadow: dark ? 'none' : LAUNCHES_THEME.cardShadow,
        }}
      >
        {ROADMAP.map((step, index) => {
          const s = BADGE_STYLES[step.tone] || BADGE_STYLES.soon;
          const isLast = index === ROADMAP.length - 1;
          return (
            <Box key={step.stage} sx={{ display: 'flex', gap: 2, pb: isLast ? 0 : 2.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, pt: 0.5 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: index === 0 ? LAUNCHES_THEME.accent : `linear-gradient(135deg, ${LAUNCHES_THEME.accent}, #22d3ee)`,
                    boxShadow: index <= 1 ? '0 0 12px rgba(6,200,109,0.55)' : 'none',
                    flexShrink: 0,
                  }}
                />
                {!isLast ? (
                  <Box sx={{ width: 3, flex: 1, background: LAUNCHES_THEME.timelineGradient, borderRadius: 4, mt: 0.5, minHeight: 32 }} />
                ) : null}
              </Box>
              <Box sx={{ flex: 1, pb: isLast ? 0 : 0.5 }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: muted }}>
                  {step.stage}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 17, color: textPrimary, mb: 0.75 }}>
                  {step.title}
                </Typography>
                <Chip
                  label={step.status}
                  size="small"
                  sx={{
                    backgroundColor: dark ? 'rgba(255,255,255,0.08)' : s.bg,
                    color: dark ? '#e2e8f0' : s.text,
                    border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : s.border}`,
                    fontFamily: FONT_FAMILY,
                    fontWeight: 600,
                    borderRadius: '999px',
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ fontFamily: FONT_FAMILY, color: muted, mb: 3, lineHeight: 1.65, maxWidth: 760 }}>
        {t('launches_vision_footer', {
          defaultValue: 'Movex no quiere ser solo una app para emergencias. Queremos ser la herramienta diaria para cualquier persona que tenga un vehículo.',
        })}
      </Typography>

      <Box
        ref={urgencyRef}
        className="ai-rise"
        sx={{
          background: dark
            ? 'linear-gradient(135deg, rgba(6,200,109,0.16) 0%, rgba(34,211,238,0.08) 100%)'
            : 'linear-gradient(135deg, rgba(6,200,109,0.12) 0%, rgba(34,211,238,0.06) 100%)',
          border: dark ? '1px solid rgba(6,200,109,0.35)' : '1px solid rgba(6,200,109,0.22)',
          borderRadius: '24px',
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          boxShadow: dark ? 'none' : '0 12px 40px rgba(6, 200, 109, 0.12)',
        }}
      >
        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: { xs: 22, md: 26 }, color: textPrimary, mb: 1 }}>
          {t('launches_urgency_title', { defaultValue: 'El momento de entrar es ahora' })}
        </Typography>
        <Typography sx={{ fontFamily: FONT_FAMILY, color: muted, mb: 2, lineHeight: 1.65 }}>
          {t('launches_urgency_text', {
            defaultValue: 'Estamos activando la red por ciudades. Los primeros proveedores podrán posicionar su perfil, construir reputación y prepararse para las nuevas categorías de servicio.',
          })}
        </Typography>
        {[
          t('launches_bullet_1', { defaultValue: 'Podrás participar en oportunidades cuando exista demanda en tu zona.' }),
          t('launches_bullet_2', { defaultValue: 'Podrás aumentar tu visibilidad dentro de la plataforma.' }),
          t('launches_bullet_3', { defaultValue: 'Podrás construir reputación desde el inicio.' }),
          t('launches_bullet_4', { defaultValue: 'Podrás preparar tu perfil antes del lanzamiento.' }),
        ].map((line) => (
          <Box key={line} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 1 }}>
            <CheckCircleIcon sx={{ color: LAUNCHES_THEME.accent, fontSize: 20, mt: 0.2 }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, color: muted, fontSize: 14, lineHeight: 1.55 }}>
              {line}
            </Typography>
          </Box>
        ))}
        <Button
          variant="contained"
          onClick={onProfileCta}
          sx={{
            ...pillBtn,
            mt: 2,
            background: `linear-gradient(135deg, ${LAUNCHES_THEME.accent} 0%, ${LAUNCHES_THEME.accentDark} 100%)`,
            color: '#fff',
          }}
        >
          {t('convert_to_driver', { defaultValue: '🚛 Activar tu grúa como socio Movex' })}
        </Button>
      </Box>

      <Typography sx={{ fontFamily: FONT_FAMILY, color: muted, fontSize: 13, lineHeight: 1.65, fontStyle: 'italic', opacity: 0.9 }}>
        {t('launches_legal', {
          defaultValue: 'Los servicios mostrados como próximos lanzamientos hacen parte de la hoja de ruta de Movex. Su disponibilidad puede variar por ciudad, proveedor, etapa de implementación y demanda real de la zona. Movex no garantiza ingresos ni volumen de solicitudes.',
        })}
      </Typography>
    </Box>
  );
}

export default function UpcomingLaunches(props) {
  const { ...rest } = props;

  return (
    <div style={{ backgroundColor: LAUNCHES_THEME.pageBg, margin: '-8px' }}>
      <Helmet>
        <title>Próximos lanzamientos Movex | Ecosistema automotriz</title>
        <meta name="description" content="Conoce la hoja de ruta de Movex: servicios móviles, Mi garaje, talleres, marketplace y Movex IA. Únete a la red de asistencia vial en Colombia." />
        <link rel="canonical" href="https://movexfinal.web.app/lanzamientos" />
      </Helmet>
      <Header
        color="white"
        routes={[]}
        rightLinks={<HeaderLinks />}
        fixed
        {...rest}
      />
      <Box sx={{ pt: { xs: 10, md: 11 }, px: { xs: 0, md: 3 }, pb: 2, maxWidth: 1120, mx: 'auto' }}>
        <UpcomingLaunchesContent />
      </Box>
      <HomeFooter />
    </div>
  );
}
